import inboxConfirmation from '@utils/inboxConfirmation'
import { getLocalStorage, setLocalStorage, removeLocalStorage } from '@utils/localStorage'

/**
 * Rules shared by the employer sign-up, sign-in and onboarding screens.
 * Values come from the Employer Auth v3 design; the server applies the same
 * rules again, these only shape the form before a request is made.
 */

export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const PERSONAL_DOMAINS = ['gmail', 'googlemail', 'yahoo', 'hotmail', 'outlook', 'icloud', 'proton', 'protonmail', 'aol', 'live', 'me', 'msn']

export const MAIL_APPS = inboxConfirmation.mailApps

export const TYPOS = {
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloook.com': 'outlook.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'iclould.com': 'icloud.com'
}

export const LINK_TTL_MINUTES = inboxConfirmation.rules.ttlMinutes
export const RESEND_COOLDOWN_SECONDS = inboxConfirmation.rules.cooldownSeconds
export const MAX_SENDS = inboxConfirmation.rules.maxSends
export const LOCK_MINUTES = inboxConfirmation.rules.lockMinutes

export const ROLES = ['Recruiter', 'Hiring manager', 'HR / People', 'Founder', 'Other']

export const ROLE_NOTES = {
  Recruiter: 'We will turn on shareable result links so hiring managers can review without an account.',
  'Hiring manager': 'We will keep setup light and show you ranked candidates first.',
  'HR / People': 'We will enable team roles and an audit trail for compliance.',
  Founder: 'We will suggest a lean two-test setup you can send today.',
  Other: ''
}

export const VOLUMES = [
  { value: '1-10', label: '1 to 10' },
  { value: '10-50', label: '10 to 50' },
  { value: '50-200', label: '50 to 200' },
  { value: '200+', label: '200+' }
]

export const VOLUME_NOTES = {
  '1-10': 'Pay-as-you-go pricing will suit you. No plan needed.',
  '10-50': 'We will enable bulk invites by CSV.',
  '50-200': 'We will enable bulk invites and suggest an ATS connection.',
  '200+': 'We will recommend a plan with unlimited results and SSO.'
}

export const TEAM_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'hiring-manager', label: 'Hiring manager' }
]

/** The glyph shown on each onboarding template tile, keyed by template key. */
export const TEMPLATE_GLYPHS = { analyst: 'Σ', sales: '%', engineer: '</>', support: '@', graduate: 'A+', manager: '&' }

export const isValidEmail = email => EMAIL.test(String(email || '').trim())

export const domainOf = email => (String(email || '').split('@')[1] || '').toLowerCase()

export const domainLabel = email => domainOf(email).split('.')[0]

export const isPersonal = email => PERSONAL_DOMAINS.includes(domainLabel(email))

/**
 * The workspace name we would infer from a work address: the first label of
 * the domain, capitalised. Empty for personal addresses.
 *
 * @param {string} email
 * @return {string}
 */
export const guessWorkspace = email => {
  const label = domainLabel(email)
  if (!label || PERSONAL_DOMAINS.includes(label)) return ''
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/**
 * A corrected address when the domain is a known misspelling, else ''.
 *
 * @param {string} email
 * @return {string}
 */
export const typoFix = email => {
  const domain = domainOf(email)
  if (!TYPOS[domain]) return ''
  return String(email).split('@')[0] + '@' + TYPOS[domain]
}

/**
 * @param {string} email
 * @return {{name: string, url: string}|null}
 */
export const mailApp = email => inboxConfirmation.mailApp(email)

/**
 * A first name to prefill from an address, e.g. avery.shah@… → Avery.
 *
 * @param {string} email
 * @return {string}
 */
export const firstNameFromEmail = email => {
  const local = String(email || '').split('@')[0].split(/[._-]/)[0]
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : ''
}

/**
 * "Chrome on macOS" style label for the cross-device screen.
 *
 * @param {string} userAgent
 * @return {string}
 */
export const deviceLabel = (userAgent = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) => {
  const ua = userAgent || ''
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
      : /Firefox\//.test(ua) ? 'Firefox'
        : /Chrome\//.test(ua) ? 'Chrome'
          : /Safari\//.test(ua) ? 'Safari' : 'a browser'
  const os = /iPhone|iPad/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
      : /Mac OS X/.test(ua) ? 'macOS'
        : /Windows/.test(ua) ? 'Windows'
          : /Linux/.test(ua) ? 'Linux' : 'an unknown device'
  return `${browser} on ${os}`
}

/**
 * "2 days ago" for the remembered-account card.
 *
 * @param {string|number} at
 * @param {number} now
 * @return {string}
 */
export const relativeTime = (at, now = Date.now()) => {
  const then = typeof at === 'number' ? at : Date.parse(at)
  if (!Number.isFinite(then)) return ''
  const minutes = Math.max(0, Math.floor((now - then) / 60000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days} ${days === 1 ? 'day' : 'days'} ago`
  const months = Math.round(days / 30)
  return `${months} ${months === 1 ? 'month' : 'months'} ago`
}

/**
 * Seconds to wait shown as "45s" or "3 min".
 *
 * @param {number} seconds
 * @return {string}
 */
export const waitLabel = seconds => {
  if (seconds > 60) return `${Math.ceil(seconds / 60)} min`
  return `${Math.max(0, Math.ceil(seconds))}s`
}

/* Remembered account: written on every successful sign-in, cleared by "Not you?". */
const REMEMBERED_KEY = 'employer.remembered'
export const readRemembered = () => {
  const value = getLocalStorage(REMEMBERED_KEY)
  return value && typeof value === 'object' && value.email ? value : null
}
export const writeRemembered = ({ email, workspace }) => {
  if (!email) return
  setLocalStorage(REMEMBERED_KEY, { email, workspace: workspace || '', at: new Date().toISOString() })
}
export const clearRemembered = () => removeLocalStorage(REMEMBERED_KEY)

/* The link request this browser asked for, so the tab can sign itself in. */
const REQUEST_KEY = 'employer.authRequest'
export const readAuthRequest = () => {
  const value = getLocalStorage(REQUEST_KEY)
  return value && typeof value === 'object' && value.id ? value : null
}
export const writeAuthRequest = request => setLocalStorage(REQUEST_KEY, request)
export const clearAuthRequest = () => removeLocalStorage(REQUEST_KEY)

/* Provider identity carried from the sign-up buttons to the workspace screen. */
const PROVIDER_KEY = 'employer.providerIdentity'
export const readProviderIdentity = () => {
  try {
    const raw = window.sessionStorage.getItem(PROVIDER_KEY)
    const value = raw ? JSON.parse(raw) : null
    return value && value.idToken ? value : null
  } catch (_) { return null }
}
export const writeProviderIdentity = identity => {
  try { window.sessionStorage.setItem(PROVIDER_KEY, JSON.stringify(identity)) } catch (_) {}
}
export const clearProviderIdentity = () => {
  try { window.sessionStorage.removeItem(PROVIDER_KEY) } catch (_) {}
}

/**
 * Turns an API failure into the sign-in screen's states.
 *
 * @param {Error} error
 * @return {{code: string, retryAfter: number, message: string}}
 */
export const describeAuthError = error => {
  const response = (error && error.response) || {}
  const data = response.data || {}
  const code = data.code || (response.status === 429 ? 'rate_limited' : response.status === 404 ? 'not_registered' : response.status ? 'server_error' : 'network')
  const retryAfter = Number(data.retryAfter || (response.headers && response.headers['retry-after']) || 0)
  const message = data.errorMessage || (data.errors && Object.values(data.errors).flat()[0]) || data.message || ''
  return { code, retryAfter, message }
}
